import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import PlayModal from "./PlayModal";

export default function HeroCarousel({ movies }: { movies: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [playOpen, setPlayOpen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const count = movies.length;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const current = movies[index];

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[460px] md:h-[620px] overflow-hidden bg-background-100"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-label="焦点影视轮播"
    >
      <div
        className="flex h-full transition-transform duration-700 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {movies.map((movie) => (
          <div key={movie.id} className="relative w-full h-full shrink-0">
            <img
              src={movie.backdrop}
              alt={`${movie.title} 剧照`}
              title={`${movie.title} 高清在线看`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40"></div>
          </div>
        ))}
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-8 lg:px-16 pt-16 md:pt-20">
          <div className="max-w-2xl animate-fade-up" key={current.id}>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-label bg-accent-500 text-background-50">
                {current.type === "movie" ? "热门电影" : "热播剧集"}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-label bg-background-50/70 text-foreground-800 border border-background-300/60">
                {current.year} · {current.region}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-label bg-primary-500/90 text-foreground-950">
                <i className="ri-star-fill mr-1 text-accent-300"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-tight">
              {current.title}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-7 clamp-3 max-w-xl">
              {current.description}
            </p>

            <p className="mt-3 text-xs md:text-sm text-foreground-500">
              主演：{current.actors.join(" / ")}　·　导演：{current.director}
            </p>

            <div className="mt-6 md:mt-8 flex items-center gap-3 flex-wrap">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-play-circle-line text-xl"></i>
                立即观看
              </Link>
              <button
                type="button"
                onClick={() => setPlayOpen(true)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/70 hover:bg-background-200 border border-background-300/70 text-foreground-900 text-sm md:text-base font-label whitespace-nowrap transition-colors cursor-pointer"
              >
                <i className="ri-tv-2-line text-xl"></i>
                播放演示
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 hover:bg-background-200 border border-background-300/60 text-foreground-900 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/60 hover:bg-background-200 border border-background-300/60 text-foreground-900 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 md:bottom-8 left-4 md:left-8 lg:left-16 flex items-center gap-2">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-3 bg-foreground-500/40 hover:bg-foreground-500/70"
            }`}
          ></button>
        ))}
      </div>

      <PlayModal open={playOpen} movie={current} onClose={() => setPlayOpen(false)} />
    </section>
  );
}